'use client'

/**
 * FareHarbor lightbox trigger button.
 *
 * Just a plain anchor pointing at the tenant's FareHarbor booking page. The
 * `autolightframe` script (loaded once in the root layout) installs a
 * document-level delegated click listener that matches any anchor whose href
 * points at `fareharbor.com/embeds/book/...` and opens the booking flow as a
 * lightbox overlay. Because it delegates off `document`, it catches both
 * server-rendered and React-rendered anchors — no per-anchor binding and no
 * `FH.open()` call. (An earlier `FH.open()` handler silently failed in
 * FareHarbor's SSR integration kit and `preventDefault`'d the click, leaving
 * the button dead.) With the script absent / JS off, the bare href navigates to
 * the booking page as a graceful fallback.
 *
 * Now `'use client'` so it can emit the funnel events on click. The onClick is
 * fire-and-forget (consent-gated `track`) and never preventDefault — the
 * autolightframe delegated listener and the bare-href fallback both keep
 * working untouched.
 */

import { track } from '@/lib/analytics'

export function FareharborLightboxButton({
  href,
  label,
  className,
}: {
  href: string
  label: React.ReactNode
  className?: string
}) {
  const labelText = typeof label === 'string' ? label : 'Book Now'
  return (
    <a
      href={href}
      className={className}
      onClick={() => {
        track('section_cta_click', { kind: 'fareharbor_booking', label: labelText, href })
        track('booking_start', { label: labelText, href })
      }}
    >
      {label}
    </a>
  )
}
